import * as Diff from 'diff';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTheme, Box, CircularProgress, Typography } from '@mui/material';
import { IOrganization } from '@tymely/atoms';
import { BaseModal } from '@tymely/components';
import {
    useOrganizationSchemaQuery,
    useOrganizationQuery,
    useSetAlert,
    useEditOrganizationMutation,
    useUser,
} from '@tymely/services';
import { CodeEditor } from '@tymely/components/CodeEditor';
import { JSONSchema7 } from 'json-schema';
import { datadogLogs } from '@datadog/browser-logs';

export const OrgConfigModal = memo(
    ({
        orgName,
        orgId,
        open,
        onClose,
    }: {
        orgName: IOrganization['name'];
        orgId: IOrganization['id'];
        open?: boolean;
        onClose?: () => void;
    }) => {
        const user = useUser();
        const { data, isLoading } = useOrganizationQuery(orgId);
        const [originalConfig, setOriginalConfig] = useState<IOrganization['config']>();
        const { data: schema, isLoading: isLoadingSchema } = useOrganizationSchemaQuery();
        const [json, setJson] = useState('');
        const [jsonSchema, setJsonSchema] = useState<JSONSchema7>();
        const theme = useTheme();
        const setAlert = useSetAlert();
        const [isSaving, setIsSaving] = useState(false);
        const [diff, setDiff] = useState<Diff.Change[]>();
        const isApproving = !!diff;

        useEffect(() => {
            if (data && schema) {
                setOriginalConfig(data.config);
                setJson(JSON.stringify(data.config, null, 4));
                setJsonSchema({
                    $defs: schema.$defs,
                    ...(schema.$defs!.OrganizationConfig as any),
                });
            }
        }, [data, schema]);

        const hasChanges = useMemo(() => JSON.stringify(originalConfig, null, 4) !== json, [originalConfig, json]);

        const editOrg = useEditOrganizationMutation();

        return (
            <BaseModal
                open={!!open}
                title={orgName}
                okLabel={isApproving ? 'Approve' : 'Save'}
                closeLabel={isApproving ? 'Edit' : 'Close'}
                loading={isSaving}
                disabled={!hasChanges}
                maxWidth="md"
                onClose={() => {
                    if (isApproving) {
                        setDiff(undefined);
                    } else {
                        onClose?.();
                    }
                }}
                onOk={async () => {
                    if (isApproving) {
                        try {
                            setIsSaving(true);
                            const orgUpdated = await editOrg.mutateAsync({
                                id: orgId,
                                config: JSON.parse(json) as IOrganization['config'],
                            });
                            datadogLogs.logger.info('Organization config modified', {
                                username: user?.username,
                                orgId: orgId,
                                oldConfig: originalConfig,
                                newConfig: orgUpdated.config,
                                diff: Diff.diffJson(originalConfig!, orgUpdated.config).filter(
                                    (change) => change.added || change.removed,
                                ),
                            });
                            onClose?.();
                        } catch (e) {
                            setAlert(`Error saving organization config: ${e}`);
                        } finally {
                            setIsSaving(false);
                        }
                    } else {
                        setDiff(Diff.diffJson(originalConfig!, JSON.parse(json) as object));
                    }
                }}
            >
                <Box display="flex" flexDirection="column">
                    {isApproving && <Typography>Please review the changes:</Typography>}
                    {isLoading || isLoadingSchema ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: theme.spacing(50),
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <CodeEditor
                            code={json}
                            diff={diff}
                            language="json"
                            jsonSchema={jsonSchema}
                            onChange={isSaving || isApproving ? undefined : setJson}
                            editorProps={{
                                maxHeight: theme.spacing(70),
                            }}
                        />
                    )}
                </Box>
            </BaseModal>
        );
    },
);

OrgConfigModal.displayName = 'OrgConfigModal';
